import React from "react";
import Box from "@mui/material/Box";
import { useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useSnackbar } from "notistack";
import { fetchClients, IClientFilter } from "../../data/Clients";
import { useQuery } from "@tanstack/react-query";
import Filter from "./ClientListFilter";
import { IAddress, addressFields } from "../../data/Address";
import ClientListTable from "./ClientListTable";
import TableToolbar from "../misc/TableToolbar";

export default function ClientListPage() {
  const { enqueueSnackbar } = useSnackbar();

  React.useEffect(() => {
    document.title = "Clients";
  }, []);

  const { control, reset, handleSubmit } = useForm<IClientFilter>({
    defaultValues: {
      search: "",
      sectors: [],
      address: null,
    },
  });

  const [searchParams, setSearchParams] = useSearchParams();

  const filter = React.useMemo(() => {
    const addressFieldValues = Object.fromEntries(
      addressFields.map((field) => [
        field,
        searchParams.get(field) ?? undefined,
      ])
    );
    const addressDefined = Object.values(addressFieldValues).some(
      (val) => val !== undefined
    );
    const address = addressDefined ? (addressFieldValues as IAddress) : null;

    return {
      search: searchParams.get("search") ?? "",
      sectors: searchParams.getAll("sectors").map(Number),
      address,
    };
  }, [searchParams]);

  React.useEffect(() => {
    reset(filter);
  }, [filter, reset]);

  const {
    data: clients,
    error,
    isLoading: clientsLoading,
  } = useQuery({
    queryKey: ["clients", searchParams.toString()],
    queryFn: () => fetchClients(searchParams.toString()),
  });

  React.useEffect(() => {
    if (error) {
      enqueueSnackbar("Failed to load clients.", { variant: "error" });
    }
  }, [error, enqueueSnackbar]);

  const handleFilter = (data: IClientFilter) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete("search");
      if (data.search) params.set("search", data.search);
      params.delete("sectors");
      data.sectors.forEach((sector) => {
        params.append("sectors", sector.toString());
      });
      addressFields.forEach((field) => {
        params.delete(field);
      });
      if (data.address) {
        const address = data.address as Record<string, string | undefined>;
        addressFields.forEach((field) => {
          const value = address[field];
          if (value) params.set(field, value);
        });
      }
      params.delete("page");
      return params;
    });
  };

  return (
    <Box
      sx={{
        display: "grid",
        borderBottom: 1,
        borderColor: "grey.300",
        gridTemplateColumns: `1fr 320px`,
        gridTemplateAreas: `"table filter"`,
      }}
    >
      <Box sx={{ gridArea: "table" }}>
        <TableToolbar name="Clients" create="/clients/create" />
        <ClientListTable
          clients={clients}
          isLoading={clientsLoading}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
        />
      </Box>
      <Box
        component="form"
        onSubmit={handleSubmit(handleFilter)}
        autoComplete="off"
        sx={{ gridArea: "filter", borderLeft: 1, borderColor: "grey.300" }}
      >
        <Filter control={control} />
      </Box>
    </Box>
  );
}
